// CS-1758 - Add New Patient Popup
// Step 1: Choose method (Manual Entry / Upload FaceSheet)
// Step 2 (facesheet only): Upload file with drag & drop, then call AI extraction API
// On extraction success, opens FacesheetExtractionPopup with pre-loaded data

import React, { useState, useRef, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PersonAddAlt1 } from "@mui/icons-material";
import { FileText, UserPlus, Upload, Users } from "lucide-react";
import { useModalStyles } from "../../common/useModalStyles";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { uploadFacesheetForExtraction } from "../../../redux/slice/AI/AiAuditSlice";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import {
  uploadIVRDocument,
  removeIVRDocument,
  IVRDocumentInfo,
} from "../../../redux/slice/ivr/ivrDocumentSlice";
import {
  setSingleAlertObj,
  SingleAlertInfo,
  showHideLoadingDialog,
  setLoadingDialogConfig,
  resetLoadingDialogConfig,
} from "../../../redux/slice/commonSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { CREATE_PATIENT_FLOW_SOURCE } from "../../../constants/applicationConstants";

type PatientAddMethod = "manual" | "facesheet";
type PopupStep = "choose" | "upload";

interface AddNewPatientPopupProps {
  open: boolean;
  onClose: () => void;
  onManualEntry: () => void;
  showFacesheetOption: boolean;
}

const ACCEPTED_FORMATS = ".pdf";//,.png,.jpg,.jpeg
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  // "image/png",
  // "image/jpeg",
  // "image/jpg",
];

const AddNewPatientPopup: React.FC<AddNewPatientPopupProps> = ({
  open,
  onClose,
  onManualEntry,
  showFacesheetOption,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { tokens, paperSx, headerSx, iconBoxSx, closeIconSx, footerSx } =
    useModalStyles();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { navigate } = useNavigation();
  const defaultSelectedMethod: PatientAddMethod = showFacesheetOption
    ? "facesheet"
    : "manual";

  const [selected, setSelected] =
    useState<PatientAddMethod>(defaultSelectedMethod);
  const [step, setStep] = useState<PopupStep>("choose");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null
  );
  const [shouldPreserveUploadedDocument, setShouldPreserveUploadedDocument] =
    useState(false);
  const [showOptionPopup, setShowOptionsPopup] =
    useState<OptionPopupObjectProps>({ message: "", show: false });
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const wasOpenRef = useRef(open);

  const cleanupUploadedDocument = useCallback(
    async (documentId?: number | null) => {
      if (!documentId) {
        return;
      }

      try {
        await dispatch(removeIVRDocument(documentId.toString()));
      } catch (error) {
        console.error("Error removing uploaded facesheet document:", error);
      }
    },
    [dispatch]
  );

  const discardUploadedFacesheet = useCallback(
    async (documentId?: number | null) => {
      await cleanupUploadedDocument(documentId);
      setSelectedFile(null);
      setSelectedDocumentId(null);
    },
    [cleanupUploadedDocument]
  );

  // CS-1758 - Reset state when popup closes and clean up abandoned uploads
  React.useEffect(() => {
    if (wasOpenRef.current && !open && selectedDocumentId && !shouldPreserveUploadedDocument) {
      void cleanupUploadedDocument(selectedDocumentId);
    }

    if (!open) {
      setSelected(defaultSelectedMethod);
      setStep("choose");
      setSelectedFile(null);
      setSelectedDocumentId(null);
      setShouldPreserveUploadedDocument(false);
      setShowOptionsPopup({ message: "", show: false });
      setIsDragOver(false);
      setUploading(false);
    }

    wasOpenRef.current = open;
  }, [
    cleanupUploadedDocument,
    defaultSelectedMethod,
    open,
    selectedDocumentId,
    shouldPreserveUploadedDocument,
  ]);

  // CS-1758 - Card styling for selection options
  const getCardSx = (isSelected: boolean) => ({
    flex: 1,
    p: 2.5,
    borderRadius: "12px",
    cursor: "pointer",
    position: "relative" as const,
    // CS-1758 - Keep border width fixed so the popup does not shift when selection changes
    border: `2px solid ${isSelected ? tokens.accent : tokens.border}`,
    boxSizing: "border-box" as const,
    backgroundColor: isSelected
      ? isDark
        ? "rgba(20, 184, 166, 0.08)"
        : "rgba(20, 184, 166, 0.04)"
      : "transparent",
    // CS-1758 - Animate only paint properties to avoid layout "shake"
    transition: "border-color 0.2s ease, background-color 0.2s ease",
    "&:hover": {
      borderColor: tokens.accent,
      backgroundColor: isDark
        ? "rgba(20, 184, 166, 0.06)"
        : "rgba(20, 184, 166, 0.03)",
    },
  });

  const readFileAsBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }, []);

  const removeDataUriPrefix = useCallback((dataUri: string): string => {
    const base64String = dataUri.split(",")[1];
    return base64String || "";
  }, []);

  const uploadFacesheetDocument = useCallback(
    async (file: File) => {
      const base64Content = await readFileAsBase64(file);

      const documentObject: IVRDocumentInfo = {
        ivrId: null,
        documentName: file.name,
        content: removeDataUriPrefix(base64Content),
        type: "IVR",
        fileType: file.type,
      };

      const response = await dispatch(uploadIVRDocument(documentObject));

      if (
        response.meta.requestStatus !== "fulfilled" ||
        !response.payload?.data?.documentId
      ) {
        const errorMessage =
          typeof response.payload === "string" && response.payload.trim() !== ""
            ? response.payload
            : "Failed to upload the selected file.";
        throw new Error(errorMessage);
      }

      return Number(response.payload.data.documentId);
    },
    [dispatch, readFileAsBase64, removeDataUriPrefix]
  );

  const handleContinue = () => {
    if (selected === "manual") {
      onManualEntry();
      onClose();
    } else {
      setStep("upload");
    }
  };

  const handleBack = () => {
    void discardUploadedFacesheet(selectedDocumentId);
    setStep("choose");
  };

  // CS-1758 - File validation
  const isValidFile = useCallback((file: File): boolean => {
    return ACCEPTED_MIME_TYPES.includes(file.type);
  }, []);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!isValidFile(file)) {
        const alertObj: SingleAlertInfo = {
          message: "Please upload a PDF file.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
        return;
      }

      setUploading(true);

      try {
        const previousDocumentId = selectedDocumentId;
        const documentId = await uploadFacesheetDocument(file);

        if (previousDocumentId) {
          await cleanupUploadedDocument(previousDocumentId);
        }

        setSelectedFile(file);
        setSelectedDocumentId(documentId);
      } catch (error: any) {
        const alertObj: SingleAlertInfo = {
          message: error.message || "Failed to upload the selected file.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
      } finally {
        setUploading(false);
      }
    },
    [
      cleanupUploadedDocument,
      dispatch,
      isValidFile,
      selectedDocumentId,
      uploadFacesheetDocument,
    ]
  );

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleFileSelect(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // CS-1758 - Drag & drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void handleFileSelect(file);
    }
  };

  const handlePopupClose = () => {
    if (!shouldPreserveUploadedDocument) {
      void discardUploadedFacesheet(selectedDocumentId);
    }
    setShowOptionsPopup({ message: "", show: false });
    onClose();
  };

  const hasNoExtractedPatientData = (facesheetResponse: any) => {
    const facesheet = facesheetResponse?.facesheet || facesheetResponse || {};
    const firstName = facesheet?.first_name?.trim?.() || "";
    const lastName = facesheet?.last_name?.trim?.() || "";
    const completenessPercent = Number(
      facesheetResponse?.completeness_percent ?? 0
    );

    // CS-1758 - Show warning when either patient name is missing or extraction completeness is zero
    // return !firstName || !lastName || completenessPercent === 0;
    return false;//Disabled the warning as of now
  };

  const showManualPatientWarningPopup = useCallback(
    (message: string) => {
      // CS-1758 - Reuse warning popup so extraction issues can continue into manual patient flow
      setShowOptionsPopup({
        message,
        show: true,
        variant: "warning",
        buttons: [
          {
            name: "Add Patient",
            color: "success",
            onClick: async () => {
              setShowOptionsPopup({ message: "", show: false });
              await discardUploadedFacesheet(selectedDocumentId);
              onClose();
              onManualEntry();
            },
          },
          {
            name: "Cancel",
            color: "secondary",
            onClick: () => setShowOptionsPopup({ message: "", show: false }),
          },
        ],
      });
    },
    [discardUploadedFacesheet, onClose, onManualEntry, selectedDocumentId]
  );

  // CS-1758 - Upload & Process: call AI facesheet extraction API
  const handleUploadAndProcess = async () => {
    if (!selectedFile || !selectedDocumentId) return;

    setUploading(true);
    dispatch(
      setLoadingDialogConfig({
        variant: "aiMedicalAudit",
        title: "Processing facesheet",
        message:
          "We are securely processing the document to extract patient demographic and insurance information.",
      })
    );
    dispatch(showHideLoadingDialog(true));

    try {
      const response = await dispatch(
        uploadFacesheetForExtraction({ documentId: selectedDocumentId })
      );

      if (response.meta.requestStatus === "fulfilled") {
        if (hasNoExtractedPatientData(response.payload?.data)) {
          showManualPatientWarningPopup(
            "No patient data is extracted from facesheet, still you can continue creating the manual patient flow."
          );
          return;
        }

        const alertObj: SingleAlertInfo = {
          message: "Facesheet data extracted successfully!",
          alertType: "success",
        };
        dispatch(setSingleAlertObj(alertObj));
        setShouldPreserveUploadedDocument(true);
        onClose();
        // CS-1758 - Navigate to patient form with extracted facesheet data and uploaded file
        navigate("/editPatient", {
          state: {
            patientDetails: {
              isPatientEdit: false,
              patientId: null,
              // CS-1758 - Facesheet add patient flow source
              createPatientFlowSource: CREATE_PATIENT_FLOW_SOURCE.FACESHEET,
              facesheetData: response.payload?.data,
              facesheetFile: selectedFile,
              facesheetDocumentId: selectedDocumentId,
            },
          },
        });
      } else {
        const errorMsg =
          typeof response.payload === "string" && response.payload.trim() !== ""
            ? response.payload
            : "Unable to process the selected file. Please upload a supported facesheet and try again.";
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      showManualPatientWarningPopup(
        "No patient data is extracted from facesheet, still you can continue creating the manual patient flow."
      );
    } finally {
      setUploading(false);
      dispatch(showHideLoadingDialog(false));
      dispatch(resetLoadingDialogConfig());
    }
  };

  // CS-1758 - Render Step 1: Choose method
  const renderChooseStep = () => (
    <>
      <Box
        sx={{
          px: 2.8,
          py: 3,
          display: "flex",
          gap: 2,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* Upload FaceSheet Card */}
        {showFacesheetOption && (
          <Box
            sx={getCardSx(selected === "facesheet")}
            onClick={() => setSelected("facesheet")}
          >
            {selected === "facesheet" && (
              <CheckCircleIcon
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  fontSize: "1.2rem",
                  color: tokens.accent,
                }}
              />
            )}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              <FileText
                size={20}
                color={tokens.textSecondary}
                strokeWidth={1.8}
              />
            </Box>
            <Typography
              fontWeight={700}
              sx={{ fontSize: "0.95rem", color: tokens.textPrimary, mb: 0.5 }}
            >
              Upload FaceSheet
            </Typography>
            <Typography
              sx={{
                fontSize: "0.78rem",
                color: tokens.textSecondary,
                lineHeight: 1.5,
              }}
            >
              Import patient data from an EMR FaceSheet document
            </Typography>
          </Box>
        )}

        {/* Manual Entry Card */}
        <Box
          sx={getCardSx(selected === "manual")}
          onClick={() => setSelected("manual")}
        >
          {selected === "manual" && (
            <CheckCircleIcon
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                fontSize: "1.2rem",
                color: tokens.accent,
              }}
            />
          )}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: isDark
                ? "rgba(255,255,255,0.06)"
                : "rgba(0,0,0,0.04)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <UserPlus
              size={20}
              color={tokens.textSecondary}
              strokeWidth={1.8}
            />
          </Box>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "0.95rem", color: tokens.textPrimary, mb: 0.5 }}
          >
            Manual Entry
          </Typography>
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: tokens.textSecondary,
              lineHeight: 1.5,
            }}
          >
            Fill in the patient details manually using our form
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={footerSx}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Box
            component="button"
            onClick={handleContinue}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              px: 3,
              py: 1.2,
              borderRadius: "10px",
              border: "none",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "0.9rem",
              color: "#ffffff",
              backgroundColor: tokens.accent,
              boxShadow: "0 4px 18px rgba(20,184,166,0.35)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#0d9488",
                boxShadow: "0 6px 22px rgba(20,184,166,0.45)",
              },
            }}
          >
            Continue
            <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
          </Box>
        </Box>
      </Box>
    </>
  );

  // CS-1758 - Render Step 2: Upload FaceSheet
  const renderUploadStep = () => (
    <>
      {/* Drag & Drop Zone */}
      <Box sx={{ px: 2.8, py: 3 }}>
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${
              isDragOver
                ? tokens.accent
                : selectedFile
                ? tokens.accent
                : tokens.border
            }`,
            borderRadius: "12px",
            p: 2.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isDragOver
              ? isDark
                ? "rgba(20, 184, 166, 0.06)"
                : "rgba(20, 184, 166, 0.03)"
              : "transparent",
            transition: "all 0.2s ease",
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.7 : 1,
          }}
          onClick={uploading ? undefined : handleBrowseClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FORMATS}
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />

          {selectedFile ? (
            <>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "10px",
                  backgroundColor: `${tokens.accent}1a`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.25,
                }}
              >
                <FileText size={24} color={tokens.accent} strokeWidth={1.8} />
              </Box>
              <Typography
                fontWeight={600}
                sx={{
                  fontSize: "0.9rem",
                  color: tokens.accent,
                  mb: 0.35,
                  textAlign: "center",
                  wordBreak: "break-all",
                }}
              >
                {selectedFile.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: tokens.textSecondary,
                }}
              >
                {uploading ? "Uploading..." : "Click or drop to replace"}
              </Typography>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "10px",
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.25,
                }}
              >
                <Upload
                  size={24}
                  color={tokens.textSecondary}
                  strokeWidth={1.8}
                />
              </Box>
              <Typography
                fontWeight={600}
                sx={{
                  fontSize: "0.9rem",
                  color: tokens.textPrimary,
                  mb: 0.35,
                  textAlign: "center",
                }}
              >
                Drag & drop your FaceSheet here
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: tokens.textSecondary,
                  mb: 1.5,
                  textAlign: "center",
                }}
              >
                {uploading ? "Uploading file..." : "Supports PDF format"}
              </Typography>
              <Box
                component="button"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleBrowseClick();
                }}
                sx={{
                  px: 2.5,
                  py: 0.8,
                  borderRadius: "8px",
                  border: `1px solid ${tokens.accent}`,
                  backgroundColor: `${tokens.accent}1a`,
                  color: tokens.accent,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: `${tokens.accent}33`,
                  },
                }}
                disabled={uploading}
              >
                Browse Files
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Footer with Back + Upload & Process */}
      <Box sx={footerSx}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box
            component="button"
            onClick={handleBack}
            disabled={uploading}
            sx={{
              flex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
              px: 3,
              py: 1.2,
              borderRadius: "10px",
              border: `1px solid rgba(239,68,68,0.4)`,
              cursor: uploading ? "not-allowed" : "pointer",
              fontWeight: 700,
              fontSize: "0.9rem",
              color: "#ef4444",
              backgroundColor: "transparent",
              transition: "all 0.2s ease",
              opacity: uploading ? 0.5 : 1,
              "&:hover": {
                borderColor: "#ef4444",
                backgroundColor: "rgba(239,68,68,0.06)",
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: "1rem" }} />
            Back
          </Box>
          <Box
            component="button"
            onClick={handleUploadAndProcess}
            disabled={!selectedFile || uploading}
            sx={{
              flex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
              px: 3,
              py: 1.2,
              borderRadius: "10px",
              border: "none",
              cursor:
                !selectedFile || uploading ? "not-allowed" : "pointer",
              fontWeight: 700,
              fontSize: "0.9rem",
              color: "#ffffff",
              backgroundColor:
                !selectedFile || uploading
                  ? isDark
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.12)"
                  : tokens.accent,
              boxShadow:
                !selectedFile || uploading
                  ? "none"
                  : "0 4px 18px rgba(20,184,166,0.35)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor:
                  !selectedFile || uploading
                    ? undefined
                    : "#0d9488",
                boxShadow:
                  !selectedFile || uploading
                    ? undefined
                    : "0 6px 22px rgba(20,184,166,0.45)",
              },
            }}
          >
            {uploading
              ? selectedDocumentId
                ? "Processing..."
                : "Uploading..."
              : "Upload & Process"}
          </Box>
        </Box>
      </Box>
    </>
  );

  // CS-1758 - Header config per step
  const headerConfig = {
    choose: {
      icon: <Users size={20} color={tokens.accent} strokeWidth={1.8} />,
      title: "Add New Patient",
      subtitle: "Choose how you'd like to add the patient",
    },
    upload: {
      icon: <FileText size={20} color={tokens.accent} strokeWidth={1.8} />,
      title: "Upload FaceSheet",
      subtitle: "Import patient data from your facesheet document",
    },
  };

  const currentHeader = headerConfig[step];

  return (
    <>
      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}
      <Dialog
        open={open}
        onClose={uploading ? undefined : handlePopupClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        {/* Header */}
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>{currentHeader.icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: "1.1rem",
                color: tokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              {currentHeader.title}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: tokens.textSecondary,
                mt: 0.15,
              }}
            >
              {currentHeader.subtitle}
            </Typography>
          </Box>
          {!uploading && (
            <IconButton onClick={handlePopupClose} size="small" sx={closeIconSx}>
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
        </Box>

        {step === "choose" ? renderChooseStep() : renderUploadStep()}
      </Dialog>
    </>
  );
};

export default AddNewPatientPopup;
